import { ISelectOption } from 'components/admin/ui';
import { ISwrConfiguration, ISwrResponse, useDebounce } from 'lib/hooks';
import { ISearchResult } from 'lib/types';
import _ from 'lodash';
import { useMemo, useState } from 'react';
import { Select } from '../../admin/ui/Form/Select';

interface ISearchQueryParams {
  offset: number;
  limit: number;
}

interface IAsyncSelectProps<T, CustomQueryParams> {
  className?: string;
  customQueryParams?: CustomQueryParams;
  defaultValue?: ISelectOption<T>;
  errorMessage?: string;
  getLabel: (value: T) => string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isVisible?: boolean;
  onChange: (value: T) => void;
  placeholder?: string;
  searchTermKey?: string;
  useSearch: (
    queryParams: CustomQueryParams & ISearchQueryParams,
    options?: ISwrConfiguration
  ) => ISwrResponse<ISearchResult<T>>;
}

export const AsyncSelect = <T, CustomQueryParams = undefined>({
  className,
  customQueryParams,
  defaultValue,
  errorMessage,
  getLabel,
  isDisabled,
  isInvalid,
  isVisible = true,
  onChange,
  placeholder = 'Search...',
  searchTermKey = 'searchTerm',
  useSearch,
}: IAsyncSelectProps<T, CustomQueryParams>) => {
  const [searchTerm, setSearchTerm] = useState<string>(undefined);
  const debouncedSearchTerm = useDebounce<string>(
    _.size(searchTerm) > 2 ? searchTerm : '',
    250
  );

  const { data, isLoading } = useSearch(
    {
      ...customQueryParams,
      [searchTermKey]: debouncedSearchTerm,
      limit: 100,
      offset: 0,
    },
    { shouldFetch: isVisible }
  );

  const options: ISelectOption[] = useMemo(
    () =>
      _.flatMap(data?.results, (x) => ({
        label: getLabel(x),
        value: x,
      })),
    [data]
  );

  return (
    <Select
      className={className}
      defaultValue={defaultValue}
      errorMessage={errorMessage}
      filterOption={_.stubTrue}
      getOptionValue={(option: ISelectOption) => option.label}
      isDisabled={isDisabled}
      isClearable
      isInvalid={isInvalid}
      isSearchable
      isLoading={isLoading}
      menuPlacement="bottom"
      onChange={(selected: ISelectOption) => onChange(selected?.value)}
      onInputChange={setSearchTerm}
      options={options}
      placeholder={placeholder}
    />
  );
};
